import React, { useState, useEffect, useContext } from 'react';
import ReactModal from 'react-modal';
import axios from 'axios';
import SglContext from '../SglContext';
import ManageCards from '../common/ManageCards';
import { LoadingSpinner } from './teamManagement/utilities';

const ChangeDefaultCardModal = ({ 
  isOpen, 
  onClose, 
  paymentMethods, 
  currentDefaultId,
  onSuccess,
  checkCardEmailPhoneInfo,
  checkAddressInfo,
}) => {
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
  const [newlyAddedCardId, setNewlyAddedCardId] = useState(null);

  const Sgl = useContext(SglContext);

  useEffect(() => {
  if (isOpen) {
    if (!newlyAddedCardId) {
      setSelectedCardId(currentDefaultId);
    }
    setIsDropdownOpen(false);
  }
  }, [isOpen, currentDefaultId, newlyAddedCardId]);

  useEffect(() => {
    if (newlyAddedCardId && paymentMethods.length > 0) {
      const newCard = paymentMethods.find(pm => 
        pm.sgl_id == newlyAddedCardId
      );
      
      if (newCard) {
        setSelectedCardId(newCard.sgl_id); 
      }
    }
  }, [paymentMethods, newlyAddedCardId]);



  const handleCardSelect = (cardId) => {
    setSelectedCardId(cardId);
    setIsDropdownOpen(false);
    
    // If they selected the current default, just close the modal
    if (cardId == currentDefaultId) {
      // onClose();
    }
  };

  const handleDefaultPaymentMethodChange = async (selectedCard) => {
    // Don't proceed if already the default
    if (currentDefaultId == selectedCard.sgl_id) {
      return;
    }

    setIsSubmitting(true);

    let createActionStep = Sgl?.pendingUserOnboardAction?.hasPendingAction && 
                          !Sgl?.pendingUserOnboardAction?.defaultPaymentMethod;

    // Create action step for authentication
    let authActionStep = createActionStep ? await axios.post(`${Sgl.nestApiUrl}/billing/createActionStepData`, {
      action: 'Pay Method',
      data: { selectedPaymentMethod: selectedCard },
      loginId: Sgl.current_user.sgl_id,
      companyId: Sgl.company_id,
      desc: 'Authenticating default payment method'
    }) : null;

    try {
      // Pre-authorize the card
      let result = await axios.get(Sgl.sglApiUrl + '/iphonev2/addentryv2/PreAuthorizeCreditCard', {
        params: {
          secured_payment_method_fingerprint_id: selectedCard.sgl_id,
          company_id: -1
        }
      });

      if (result?.data != '') {
        alert(result.data);
        if (authActionStep?.data?.sgl_id > 0) {
          axios.post(`${Sgl.nestApiUrl}/billing/completeActionStepData`, {
            actionStepId: authActionStep?.data?.sgl_id,
            status: 'Failure',
            desc: `Payment method ${selectedCard.name_on_card} xxxx-${selectedCard.card_lastfour} authentication failed`,
            error: result.data
          });
        }
        setIsSubmitting(false);
        return;
      }
    } catch (err) {
      console.error("Error validating payment method:", err);
      alert('Error validating payment method: ' + err.message);
      if (authActionStep?.data?.sgl_id > 0) {
        axios.post(`${Sgl.nestApiUrl}/billing/completeActionStepData`, {
          actionStepId: authActionStep?.data?.sgl_id,
          status: 'Failure',
          desc: `Payment method ${selectedCard.name_on_card} xxxx-${selectedCard.card_lastfour} authentication failed`,
          error: err.message
        });
      }
      setIsSubmitting(false);
      return;
    }

    // Log successful authentication
    if (authActionStep?.data?.sgl_id > 0) {
      axios.post(`${Sgl.nestApiUrl}/billing/completeActionStepData`, {
        actionStepId: authActionStep?.data?.sgl_id,
        status: 'Complete',
        desc: `Default payment method ${selectedCard.name_on_card} xxxx-${selectedCard.card_lastfour} authenticated successfully`,
        error: ''
      });
    }

    // Create action step for setting default
    let respActionStep = createActionStep ? await axios.post(`${Sgl.nestApiUrl}/billing/createActionStepData`, {
      action: 'Pay Method',
      data: { selectedPaymentMethod: selectedCard },
      loginId: Sgl.current_user.sgl_id,
      companyId: Sgl.company_id,
      desc: 'Setting default payment method'
    }) : null;

    // Update default payment method
    try {
      const res = await axios.post(`${Sgl.nestApiUrl}/billing/updateDefaultCreditCard`, {
        user_id: Sgl.current_user.sgl_id,
        payment_method_id: selectedCard.sgl_id,
        company_id: Sgl.company_id
      });

      if (res.data.success) {
        if (respActionStep?.data?.sgl_id > 0) {
          axios.post(`${Sgl.nestApiUrl}/billing/completeActionStepData`, {
            actionStepId: respActionStep?.data?.sgl_id,
            status: 'Complete',
            desc: `Default payment method ${selectedCard.name_on_card} xxxx-${selectedCard.card_lastfour} set successfully`,
            error: ''
          });
        }

        // Success - close modal and refresh parent
        setIsSubmitting(false);
        onSuccess();
        onClose();
      } else {
        alert(res.data.error);
        if (respActionStep?.data?.sgl_id > 0) {
          axios.post(`${Sgl.nestApiUrl}/billing/completeActionStepData`, {
            actionStepId: respActionStep?.data?.sgl_id,
            status: 'Failure',
            desc: `Failed to set default payment method ${selectedCard.name_on_card} xxxx-${selectedCard.card_lastfour}.`,
            error: res.data.error
          });
        }
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error updating default card:', error);
      alert('Error updating default payment method: ' + error.message);
      setIsSubmitting(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedCardId) return;
    
    // If same card selected, just close modal
    if (selectedCardId == currentDefaultId) {
      onClose();
      return;
    }
    
    const selectedCard = paymentMethods.find(card => card.sgl_id == selectedCardId);
    if (selectedCard) {
      handleDefaultPaymentMethodChange(selectedCard);
    }
  };

  const selectedCard = paymentMethods.find(card => card.sgl_id == selectedCardId);
  const hasChanged = selectedCardId != currentDefaultId;

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modern-team-modal"
      overlayClassName="modern-team-overlay"
      ariaHideApp={false}
    >
      <div className="modern-modal-header">
        <h2>Change Default Payment Method</h2>
        <button 
          className="modern-close-btn" 
          onClick={onClose}
          disabled={isSubmitting}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      <div className="modern-modal-body">
        <div className="modern-form-group">
          <label>Select Payment Method</label>
          <div className="custom-dropdown">
            <div 
              className={`custom-dropdown-trigger ${isDropdownOpen ? 'open' : ''} ${isSubmitting ? 'disabled' : ''}`}
              onClick={() => !isSubmitting && setIsDropdownOpen(!isDropdownOpen)}
            >
              <div className="dropdown-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M2 10h20" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <span className="dropdown-text">
                {selectedCard 
                  ? `${selectedCard.type} ending in ${selectedCard.card_lastfour}` 
                  : 'Select a payment method...'}
              </span>
              <div className="dropdown-arrow">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
            </div>
            
            {isDropdownOpen && !isSubmitting && (
              <div className="custom-dropdown-menu">
                {paymentMethods.map((card) => (
                  <div
                    key={card.sgl_id}
                    className={`dropdown-option ${selectedCardId == card.sgl_id ? 'selected' : ''}`}
                    onClick={() => handleCardSelect(card.sgl_id)}
                  >
                    <div className="option-name">
                      {card.type} •••• {card.card_lastfour}
                    </div>
                    <div className="option-description">
                      {card.name_on_card} • Expires {card.card_exp}
                    </div>
                    {selectedCardId == card.sgl_id && (
                      <div className="option-check">✓</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedCard && (
          <div className="modern-form-group" style={{ 
            background: '#f9fafb', 
            border: '1px solid #e5e7eb', 
            borderRadius: '8px', 
            padding: '16px',
            marginTop: '16px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Cardholder:</span>
              <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{selectedCard.name_on_card}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Card Type:</span>
              <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{selectedCard.type}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Expires:</span>
              <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>{selectedCard.card_exp}</span>
            </div>
          </div>
        )}

        <div className="modern-form-group">
          <button 
            className="modern-btn-secondary"
            onClick={() => setIsAddCardModalOpen(true)}
            disabled={isSubmitting}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px 16px',
              border: '1px dashed #d1d5db',
              background: 'transparent',
              color: '#0ea5e9',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Add New Payment Method
          </button>
        </div>
      </div>

      <div className="modern-modal-footer">
        <button 
          className="modern-btn-cancel" 
          onClick={onClose}
          disabled={isSubmitting}
        >
          Close
        </button>
            {hasChanged && (
              <button 
                className="modern-btn-primary" 
                onClick={handleConfirm}
                disabled={isSubmitting || !selectedCardId}
              >
                {isSubmitting ? (
                  <LoadingSpinner message="" />
                ) : (
                  'Confirm'
                )}
              </button>
            )}
      </div>

        {/* ManageCards Modal for Adding New Card */}
        {isAddCardModalOpen && (
        <ManageCards
            payment_methods={paymentMethods}
            check_card_email_phone_info={checkCardEmailPhoneInfo}
            check_address_info={checkAddressInfo}
            is_open_manage_card_modal={false}
            is_open_add_card_modal={true}
            closeManageCardModal={async (newCardId) => {
              setIsAddCardModalOpen(false);
              
              if (newCardId) {
                setNewlyAddedCardId(newCardId);
                await onSuccess();
              } else {
                onSuccess();
              }
            }}
            getPaymentMethods={onSuccess}
            showDefaultSelection={false}
        />
        )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .custom-dropdown-trigger.disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </ReactModal>
  );
};

export default ChangeDefaultCardModal;